import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { isPlatformBrowser } from '@angular/common';
import { OmnitureService } from 'careerpages-base/services/omniture.service';
import { SiteService } from '../site.service';
import { CookiesService } from '@ngx-utils/cookies';

@Injectable()
export class ReportingService {

  // User data
  public userGuid: string;
  public userJsid: string;
  public isNewUser: boolean;
  public previousEvent: string;

  // Event specific tracking
  public jobListingIndex: number = null;

  // Service declaration
  private uuidService = require('uuid/v4');

  // Constants for cookies
  private static COOKIE_UUID: string = 'CP_UUID';
  private static COOKIE_EXPIRE: number = 30; // Number of days until cookie expires
  private static COOKIE_SEEKER_GUID: string = 'sajWTM';
  private static COOKIE_SEEKER_JSID: string = 'jobseeker';

  constructor(
    private activatedRoute: ActivatedRoute,
    private http: HttpClient,
    private omnitureService: OmnitureService,
    private siteService: SiteService,
    private cookieService: CookiesService,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {
    this.isNewUser = false;
    this.userJsid = this.getUserSeekerId();
    this.userGuid = this.getUserGuid();
  }

  /**
   * Gets the Job Seeker ID if available, or null
   * @returns {string} - JobSeeker ID that snagajob.com would use
   */
  getUserSeekerId() {
    // Return if we have it from Seeker
    if (isPlatformBrowser(this.platformId) && this.cookieService.get(ReportingService.COOKIE_SEEKER_JSID)) {
      return this.cookieService.get(ReportingService.COOKIE_SEEKER_JSID);
    }

    // Otherwise return null
    return null;
  }

  /**
   * Checks the users cookies if they have a UUID set and use it
   * @returns {string} - User GUID, either existing or newly generated
   */
  getUserGuid(): string {
    if (isPlatformBrowser(this.platformId)) {
      if (this.cookieService.get(ReportingService.COOKIE_UUID)) {
        return this.cookieService.get(ReportingService.COOKIE_UUID);
      }

      // If we have a GUID from Seeker, return it
      if (this.cookieService.get(ReportingService.COOKIE_SEEKER_GUID)) {
        return this.cookieService.get(ReportingService.COOKIE_SEEKER_GUID);
      }

      // Otherwise, generate a new UUID, store it, and return it
      this.isNewUser = true;
      const newUuid = this.uuidService();
      // set cookie expiration date
      const expires = new Date();
      expires.setDate(expires.getDate() + ReportingService.COOKIE_EXPIRE);
      this.cookieService.put(ReportingService.COOKIE_UUID, newUuid, { expires });
      return newUuid;
    } else {
      return null;
    }
  }

  /**
   * Adds user information to the data object
   * @param data {any} - data object that various services will use to make API calls
   */
  addUserData(data: any) {
    // Set the user guid if we have one
    if (this.userGuid) {
      data['eVar1'] = this.userGuid;
      data['prop1'] = this.userGuid;
    }

    // Set the seeker id if we have one
    if (this.userJsid) {
      data['eVar4'] = this.userJsid;
      data['prop4'] = this.userJsid;
    }
  }

  /**
   * Adds general site information to the request
   * @param data {any} - data that will get added to every omniture request
   */
  addSiteData(data: any) {
    // Career Page name
    let careerPageName = this.siteService.domainName;
    if (this.siteService.siteSlug) careerPageName += '_' + this.siteService.siteSlug;

    data['eVar49'] = careerPageName;
    data['prop49'] = careerPageName;
  }

  pageView(data?: any) {
    this.addUserData(data);
    this.addSiteData(data);
    this.omnitureService.pageEvent(data);
  }

  trackCustomEvent(events: string, element?: any) {
    this.omnitureService.clickEvent(events, element);
  }

  /**
   * Helper method to store which search result was clicked
   * @param index {number} - the index of the clicked search result
   */
  setJobPostingIndex(index: number) {
    this.jobListingIndex = index;
  }

  /**
   * Used to clear the job posting index after an event fire
   */
  clearJobPostingIndex() {
    this.jobListingIndex = null;
  }

  /**
   * Processes internal event handling. Will build common properties and pass to API.
   * @param data {any} - data object that gets sent to internal reporting
   */
  internalEvent(data?: any) {

    // Constants
    data['Source'] = 'Career Pages';
    data['SendToWarehouse'] = 'true';
    data['SendToElasticSearch'] = 'true';
    data['SendToStatsD'] = 'true';

    // Absolute URL
    data['Url'] = window.location.href;

    // User data
    data['UserGuid'] = this.userGuid;
    data['NewVisit'] = this.isNewUser;

    // Session data
    data['SessionId'] = null; // To be added later
    data['SessionEventId'] = this.uuidService();
    data['SessionParentEventId'] = this.previousEvent;

    // Fetch all query params that are available
    const queryParams = this.activatedRoute.snapshot.queryParams;
    if (queryParams['refcode']) {
      data['RefCode'] = queryParams['refcode'];
    }

    // ABC Tests the user is in, to be expanded on later
    data['TestGroupAssignments'] = null;

    // Set the previous event and fire the tracking call
    this.previousEvent = data['SessionEventId'];

    // Send the API call
    // Parse as text for IE11, see: https://github.com/angular/angular/issues/18680#issuecomment-330425866
    const url = `${this.siteService.getApiUrl()}/events`;
    this.http.post(url, data, {
      headers: new HttpHeaders().set('Content-Type', 'application/json'),
      responseType: 'text'
    }).subscribe();

  }

}
